/**
 * RecentQuotesPicker — compact horizontal card strip for embedding above PDF
 * upload drop zones. Shows all users' recent manual + automated quotes in tabs,
 * defaulting to the current user's own tab.
 *
 * Cards already uploaded to CRM are highlighted in blue.
 * Clicking a card passes a pre-built quoteForm object to onSelect().
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import { Loader2, Clock, ExternalLink } from 'lucide-react';
import LinkCrmAccountModal from './LinkCrmAccountModal';

const API = process.env.REACT_APP_BACKEND_URL;
const ORANGE = '#ee7203';

/** Convert any item (manual or automation) to the standard quoteForm shape */
function buildQuoteForm(item) {
  if (item.source === 'automation') {
    const e = item.email_data || {};
    const dateStr = item.last_saved || '';
    return {
      _localItemId: item.id,
      customer_name: e.invoice_customer_name || e.customer_name || '',
      customer_address: e.invoice_address || '',
      quote_number: e.order_number || '',
      reference: e.reference || '',
      date: dateStr ? new Date(dateStr).toISOString().split('T')[0] : '',
      valid_until: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      items: (e.product_lines || []).map(l => ({
        code: l.product_code || '',
        description: l.description || '',
        quantity: l.qty || 1,
        unit: 'Each',
        unit_price: l.unit_price || 0,
      })),
      notes: '',
      terms: '',
    };
  }
  // Embed the MongoDB _id so link-quote can match the right record
  return { _localItemId: item.id, ...(item.quote_data || {}) };
}

function MiniCard({ item, onSelect, onCustomerNameClick, onHistoryClick }) {
  const isAutomation = item.source === 'automation';
  const inCrm = !!item.zoho_quote_url;
  const qf = buildQuoteForm(item);

  const customerName = isAutomation
    ? (item.email_data?.invoice_customer_name || item.email_data?.customer_name || 'Unknown')
    : (qf.customer_name || 'Unnamed');
  const refNum = isAutomation
    ? (item.email_data?.order_number || item.email_data?.reference || '—')
    : (qf.quote_number || '—');
  const dateStr = item.last_saved || '';
  const lineCount = isAutomation
    ? (item.email_data?.product_lines?.length ?? 0)
    : (qf.items?.length ?? 0);

  const isAmended = item.is_amended === true;
  const [showLink, setShowLink] = useState(false);
  const [linked, setLinked] = useState(false);
  const needsCrmLink = isAutomation && !inCrm && !linked
    && (item.email_data?.crm_link_needed === true || !item.email_data?.zoho_account_id);

  return (
    <div
      className="flex-shrink-0 w-44 text-left rounded-xl p-2.5 transition-all border"
      style={inCrm
        ? { background: '#dbeafe', borderColor: '#93c5fd' }
        : { background: '#fff', borderColor: '#e5e7eb' }
      }
      data-testid={`recent-picker-card-${item.id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1 flex-wrap">
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
            style={inCrm
              ? { background: '#bfdbfe', color: '#1d4ed8', border: '1px solid #93c5fd' }
              : isAutomation
                ? { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }
                : { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }
            }
          >{inCrm ? 'In CRM' : isAutomation ? 'Auto' : 'Manual'}</span>
          {isAmended && (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: '#fefce8', color: '#92400e', border: '1px solid #fde68a' }}
            >Amended</span>
          )}
          {needsCrmLink && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowLink(true); }}
              className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              title="This customer isn't linked to a CRM account yet — click to link it"
              data-testid={`picker-no-crm-${item.id}`}
            >NO CRM · ADD NOW</button>
          )}
        </div>
        {/* View in CRM link — clickable when in CRM */}
        {inCrm && item.zoho_quote_url ? (
          <a
            href={item.zoho_quote_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="flex items-center gap-0.5 text-[9px] font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
            data-testid={`picker-view-crm-${item.id}`}
            title="View in CRM"
          >
            <ExternalLink className="w-3 h-3" /> CRM
          </a>
        ) : isAmended && onHistoryClick ? (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onHistoryClick(item); }}
            className="text-[9px] font-semibold text-amber-700 hover:text-amber-900 hover:underline transition-colors"
            data-testid={`picker-history-${item.id}`}
            title="View amendment history"
          >History</button>
        ) : null}
      </div>
      <LinkCrmAccountModal
        isOpen={showLink}
        onClose={() => setShowLink(false)}
        item={item}
        onLinked={() => { setLinked(true); window.dispatchEvent(new Event('c30:quotes-changed')); }}
      />
      {/* Customer name — clickable → opens Customer Management */}
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onCustomerNameClick && onCustomerNameClick(customerName); }}
        className={`font-semibold text-xs leading-tight truncate w-full text-left transition-colors hover:underline ${
          inCrm ? 'text-blue-900 hover:text-blue-700' : 'text-gray-900 hover:text-blue-700'
        }`}
        data-testid={`picker-customer-name-${item.id}`}
        title="Open Customer Management"
      >
        {customerName}
      </button>
      {/* Quote ref + line count — clicking selects the quote */}
      <button
        type="button"
        onClick={() => onSelect(qf)}
        className="w-full text-left"
        data-testid={`picker-select-${item.id}`}
      >
        <div className={`text-[10px] font-mono truncate mt-0.5 ${inCrm ? 'text-blue-700' : 'text-gray-500'}`}>
          {refNum}
        </div>
        <div className={`flex items-center justify-between mt-1.5 text-[10px] ${
          inCrm ? 'text-blue-600' : 'text-gray-400'
        }`}>
          <span>{lineCount} line{lineCount !== 1 ? 's' : ''}</span>
          {dateStr && (
            <span>{new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
          )}
        </div>
      </button>
    </div>
  );
}

// variant 'cards' (the default) is the strip of recent quote cards used above the PDF upload boxes. variant 'customers' is Customer Management's search screen:
// the customers those recent quotes were for, once each, as equal oblong buttons that show only the customer's name (press one to open that customer).
export default function RecentQuotesPicker({ onSelect, onCustomerNameClick, onHistoryClick, variant = 'cards' }) {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api
        .get(`${API}/api/quotes/recent/all`)
        .catch(() => ({ data: { quotes: [], current_user_id: null } }));
      const quotes = (res.data.quotes || [])
        .map(q => ({ ...q, source: q.source || 'manual' }))
        .sort((a, b) => new Date(b.last_saved) - new Date(a.last_saved));
      setAllItems(quotes);
      const uid = res.data.current_user_id;
      setCurrentUserId(uid);
      const hasOwn = quotes.some(q => q.user_id === uid);
      setActiveTab(prev => prev || (uid && hasOwn ? uid : (quotes[0]?.user_id || null)));
    } catch { /* non-fatal */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  // Refresh when a quote is added to CRM
  useEffect(() => {
    const handler = () => fetchQuotes();
    window.addEventListener('c30:quotes-changed', handler);
    return () => window.removeEventListener('c30:quotes-changed', handler);
  }, [fetchQuotes]);

  const userTabs = useMemo(() => {
    const map = {};
    allItems.forEach(q => {
      if (!map[q.user_id]) {
        map[q.user_id] = { user_id: q.user_id, label: q.user_label || q.user_id };
      }
    });
    return Object.values(map).sort((a, b) => {
      if (a.user_id === currentUserId) return -1;
      if (b.user_id === currentUserId) return 1;
      return (a.label || '').localeCompare(b.label || '');
    });
  }, [allItems, currentUserId]);

  const visibleItems = useMemo(
    () => allItems.filter(q => q.user_id === activeTab).slice(0, 20),
    [allItems, activeTab]
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {variant === 'customers' ? 'Loading recent customers…' : 'Loading recent quotes…'}
      </div>
    );
  }

  if (allItems.length === 0) return null;

  if (variant === 'customers') {
    // The customers on the chosen person's recent quotes (newest first), each once. Two spellings of one name count as one customer.
    const seen = new Set();
    const recentCustomers = [];
    allItems.filter((q) => q.user_id === activeTab).forEach((item) => {
      const emailData = item.email_data || {};
      const quoteData = item.quote_data || {};
      const name = String((item.source === 'automation' ? (emailData.invoice_customer_name || emailData.customer_name) : quoteData.customer_name) || '').trim();
      const key = name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      recentCustomers.push(name);
    });
    return (
      <div className="v3-rc space-y-2" data-testid="recent-customers-picker">
        {userTabs.length > 1 && (
          <div className="flex gap-1 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
            {userTabs.map((tab) => {
              const isActive = activeTab === tab.user_id;
              const isOwn = tab.user_id === currentUserId;
              return (
                <button
                  key={tab.user_id}
                  type="button"
                  onClick={() => setActiveTab(tab.user_id)}
                  className={`v3-rc-tab ${isActive ? 'v3-rc-tab--on' : ''} flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors whitespace-nowrap`}
                  style={isActive
                    ? { background: ORANGE, borderColor: ORANGE, color: 'white' }
                    : { background: '#f9fafb', borderColor: '#e5e7eb', color: '#6b7280' }
                  }
                  data-testid={`picker-tab-${tab.user_id}`}
                >
                  {tab.label}{isOwn ? ' (me)' : ''}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          Recent customers
        </div>
        <div className="v3-rc-grid grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
          {recentCustomers.map((name, index) => (
            <button
              key={name}
              type="button"
              onClick={() => onCustomerNameClick && onCustomerNameClick(name)}
              title={name}
              className="v3-rc-btn h-10 w-full px-3 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-800 hover:border-orange-300 hover:bg-orange-50 transition-colors flex items-center justify-center"
              data-testid={`recent-customer-${index}`}
            >
              <span className="truncate">{name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="recent-quotes-picker">
      {/* User tabs — only shown when more than one user has quotes */}
      {userTabs.length > 1 && (
        <div className="flex gap-1 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
          {userTabs.map(tab => {
            const isActive = activeTab === tab.user_id;
            const isOwn = tab.user_id === currentUserId;
            return (
              <button
                key={tab.user_id}
                type="button"
                onClick={() => setActiveTab(tab.user_id)}
                className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors whitespace-nowrap"
                style={isActive
                  ? { background: ORANGE, borderColor: ORANGE, color: 'white' }
                  : { background: '#f9fafb', borderColor: '#e5e7eb', color: '#6b7280' }
                }
                data-testid={`picker-tab-${tab.user_id}`}
              >
                {tab.label}{isOwn ? ' (me)' : ''}
              </button>
            );
          })}
        </div>
      )}

      {/* Strip header */}
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
        <Clock className="w-3.5 h-3.5 text-gray-400" />
        Select a recent quote
        <span className="text-[10px] text-gray-400 font-normal ml-1">— blue = already in CRM</span>
      </div>

      {/* Card strip */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
        {visibleItems.map(item => (
          <MiniCard key={item.id} item={item} onSelect={onSelect} onCustomerNameClick={onCustomerNameClick} onHistoryClick={onHistoryClick} />
        ))}
      </div>
    </div>
  );
}
